import Select from 'react-select';
import { toast } from 'react-toastify';
import Masonry from 'react-masonry-css';
import { TextField, Checkbox } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Container, Row, Col, Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, InputGroup, InputGroupText, Form, FormGroup, Label, Alert, UncontrolledTooltip, Modal, ModalHeader, ModalBody, ModalFooter, Table, ButtonGroup, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Collapse } from 'reactstrap';

import './Plans.css';
import axios from '../../../store/axios';
import PlanEditModal from './PlanEditModal';
import { formatNumber } from '../../../utils/formatNumber';
import { getAdminPlans, togglePlanStatus } from '../../../store/Admin';
import { PLAN_TYPE_PILL_LABELS, INTERVAL_PILL_LABELS } from './planLabels';

const Plans = ({ isAgencyView = false, workspaceId = null, accessType }) => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { adminPlans, loading: adminPlansLoading, workspaces } = useSelector(state => ({ adminPlans: state['Admin']['adminPlans'], loading: state['Admin']['loading'], workspaces: state['Admin']['workspaces'] || [] }));

  // Gateway states
  const [formData, setFormData] = useState({ secretKey: '' });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [gatewayLoading, setGatewayLoading] = useState(true);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isGatewayCollapsed, setIsGatewayCollapsed] = useState(false);
  const breakpoints = { default: 4, 1460: 3, 1200: 2, 700: 1 };

  // Plans states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeChips, setTypeChips] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedPlans, setExpandedPlans] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSourcePlan, setCreateSourcePlan] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [viewMode, setViewMode] = useState('card');
  const [agencyPlans, setAgencyPlans] = useState([]);
  const [loadingAgencyPlans, setLoadingAgencyPlans] = useState(false);

  // Business plans for agency (dynamically generated)
  const [businessPlans, setBusinessPlans] = useState([]);
  const [loadingBusinessPlans, setLoadingBusinessPlans] = useState(false);
  const [showPriceEditModal, setShowPriceEditModal] = useState(false);
  const [selectedPlanForPriceEdit, setSelectedPlanForPriceEdit] = useState(null);

  // Agency plan assignment states removed - no longer needed

  // Admin plan assignment states (for non-agency users)
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPlanForAssign, setSelectedPlanForAssign] = useState(null);
  const [selectedWorkspaceForAssign, setSelectedWorkspaceForAssign] = useState(null);
  const [assignmentReason, setAssignmentReason] = useState('');
  const [assignmentLoading, setAssignmentLoading] = useState(false);

  // Workspace-targeted purchase link (attach a custom plan to an existing workspace)
  const [showWsLinkModal, setShowWsLinkModal] = useState(false);
  const [wsLinkPlan, setWsLinkPlan] = useState(null);
  const [wsLinkWorkspace, setWsLinkWorkspace] = useState(null);

  // Load gateway status and plans on mount
  useEffect(() => {
    const initializeData = async () => {
      // First fetch gateway status
      await fetchGatewayStatus();

      if (!isAgencyView) {
        loadPlans();
      }
      // For agency view, plans will load via the gatewayStatus useEffect below
      loadWorkspaces();
    };

    initializeData();
  }, [isAgencyView, workspaceId]);

  // Load business plans when gateway becomes configured
  useEffect(() => {
    if (gatewayStatus?.['isConfigured']) {
      loadBusinessPlans();
      loadAgencyPlans();
    }
  }, [gatewayStatus?.['isConfigured']]);

  // Fetch gateway status
  const fetchGatewayStatus = async () => {
    setGatewayLoading(true);
    try {
      const wsId = workspaceId || workspaceId || currentWorkspace?.['_id'] || '';
      if (!wsId) {
        setGatewayLoading(false);
        return;
      }

      const response = await axios.get(`/payment/gateway/status/${wsId}?_=${Date.now()}`);
      if (response['data']['code'] === 200) {
        const statusData = response['data']['body'];
        setGatewayStatus(statusData);
        console.log('Gateway status loaded:', statusData);
      }
    } catch (error) {
      console.error('Error fetching gateway status:', error);
      setGatewayStatus(null);
    } finally {
      setGatewayLoading(false);
    }
  };

  // Load plans based on access type
  const loadPlans = () => {
    if (!isAgencyView) dispatch(getAdminPlans());
  };

  // Load workspaces for assignment
  const loadWorkspaces = async () => {
    try {
      const response = await axios.get('/admin/users/workspaces');
      // Will be handled by redux store
    } catch (error) {
      console.error('Error loading workspaces:', error);
    }
  };

  // Validate form inputs
  const validateForm = () => {
    const newErrors = {};

    if (!formData['secretKey']) newErrors['secretKey'] = 'Secret key is required';
    else if (!/^sk_(test|live)_[a-zA-Z0-9]{24,}$/.test(formData['secretKey']))
      newErrors['secretKey'] = 'Invalid secret key format (should start with sk_test_ or sk_live_)';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setValidating(true);
    try {
      const wsId = workspaceId || workspaceId || currentWorkspace?.['_id'] || '';
      const response = await axios.post(`/payment/gateway/configure/${wsId}`, formData);

      if (response['data']['code'] === 200) {
        const { plansGenerated = 0 } = response['data']['body'] || {};

        toast.success(
          plansGenerated > 0
            ? `Payment gateway configured successfully! ${plansGenerated} business plans have been generated.`
            : 'Payment gateway configured successfully!'
        );

        // Update gateway status
        setGatewayStatus({ ...response['data']['body'], isConfigured: true });

        // Clear form
        setFormData({ secretKey: '' });

        // Force reload of plans and gateway status
        fetchGatewayStatus();

        if (isAgencyView) {
          setTimeout(() => {
            loadBusinessPlans(true);
            loadAgencyPlans();
          }, 1000);
        }
      } else {
        toast.error(response['data']['message'] || 'Failed to configure gateway');
      }
    } catch (error) {
      console.error('Configuration error:', error);
      const errorMessage = error['response']?.['data']?.['message'] || 'Failed to configure payment gateway';
      toast.error(errorMessage);

      if (error['response']?.['status'] === 400 && error['response']?.['data']?.['message']) {
        const message = error['response']['data']['message'].toLowerCase();
        if (message.includes('secret key')) setErrors({ ...errors, secretKey: error['response']['data']['message'] });
      }
    }
    setValidating(false);
  };

  // Handle validate gateway
  const handleValidate = async () => {
    setValidating(true);
    try {
      const wsId = workspaceId || workspaceId || currentWorkspace?.['_id'] || '';
      const response = await axios.post(`/payment/gateway/validate/${wsId}`);

      if (response['data']['code'] === 200 && response['data']['body']?.['isValid']) {
        const { plansGenerated = 0 } = response['data']['body'] || {};

        toast.success(
          plansGenerated > 0
            ? `Gateway validated successfully! ${plansGenerated} business plans have been generated.`
            : 'Gateway validated successfully!'
        );

        // Update validation status
        setGatewayStatus(prev => ({ ...prev, validationStatus: 'valid', lastValidated: new Date() }));

        // Reload business plans after validation
        if (isAgencyView) {
          setTimeout(() => {
            loadBusinessPlans(true);
            loadAgencyPlans();
          }, 1000);
        }
      } else {
        toast.error(response['data']['body']?.['error'] || response['data']['message'] || 'Gateway validation failed');
      }
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Failed to validate gateway');
    }
    setValidating(false);
  };

  // Handle remove gateway
  const handleRemoveGateway = async () => {
    setLoading(true);
    try {
      const wsId = workspaceId || workspaceId || currentWorkspace?.['_id'] || '';
      const response = await axios.delete(`/payment/gateway/${wsId}`);

      if (response['data']['code'] === 200) {
        toast.success('Payment gateway removed successfully');
        setGatewayStatus(null);
        setBusinessPlans([]); // Clear business plans when gateway is removed
        setShowRemoveModal(false);
        fetchGatewayStatus();
      } else {
        toast.error(response['data']['message'] || 'Failed to remove gateway');
      }
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove payment gateway');
    }
    setLoading(false);
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e['target'];
    setFormData(prev => ({ ...prev, [name]: value.trim() }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Handle copy webhook
  const handleCopyWebhook = () => toast.success('Webhook URL copied to clipboard!');

  // Load agency plans
  const loadAgencyPlans = async () => {
    if (!workspaceId && !currentWorkspace?.['_id']) return;

    setLoadingAgencyPlans(true);
    try {
      const wsId = workspaceId || currentWorkspace?.['_id'];
      const response = await axios.get(`/payment/plans/agency/${wsId}`);

      console.log('Agency plans loaded:', {
        workspace: wsId,
        response: response['data']
      });

      if (response['data']['code'] === 200) {
        const plans = response['data']['body'] || [];
        setAgencyPlans(plans);
      }
    } catch (error) {
      console.error('Error loading agency plans:', error);
      toast.error('Failed to load agency plans');
    }
    setLoadingAgencyPlans(false);
  };

  // Load business plans for agency - only if Stripe is configured
  const loadBusinessPlans = async (skipStripeCheck = false) => {
    // Don't load business plans for non-agency views
    if (!isAgencyView) return;

    console.log('🔄 Attempting to load business plans...', {
      isAgencyView,
      workspaceId,
      currentWorkspaceId: currentWorkspace?.['_id'],
      gatewayStatus,
      skipStripeCheck
    });

    const wsId = workspaceId || currentWorkspace?.['_id'];

    console.log('Loading business plans for workspace:', {
      wsId,
      currentWorkspace: currentWorkspace?.['_id'],
      workspaceId
    });

    // Check if we have a valid workspace
    if (!wsId) {
      console.error('No workspace ID available');
      toast.error('No workspace ID available. Please check your agency configuration.');
      return;
    }

    // Only check Stripe configuration if not skipping (after successful config)
    if (!skipStripeCheck && (!gatewayStatus?.['isConfigured'] || gatewayStatus?.['validationStatus'] !== 'valid')) {
      console.log('⚠️ Stripe not configured or validated, skipping business plan load', gatewayStatus);
      setBusinessPlans([]);
      return;
    }

    setLoadingBusinessPlans(true);
    try {
      const response = await axios.get(`/payment/agencyPlans/${wsId}`);

      console.log('📋 Business plans API response:', response['data']);

      if (response['data']['code'] === 200) {
        const plans = response['data']['body'] || [];
        console.log(`✅ Loaded ${plans.length} business plans`);
        setBusinessPlans(plans);
      } else {
        console.error('Business plans API returned error:', response['data']['message']);
        toast.error('Failed to load business plans: ' + response['data']['message']);
      }
    } catch (error) {
      console.error('Error loading business plans for agency:', error);
      console.error('Error details:', {
        message: error['message'],
        status: error['response']?.['status'],
        statusText: error['response']?.['statusText'],
        responseData: error['response']?.['data']
      });

      // Only show error if Stripe is configured
      if (gatewayStatus?.['isConfigured']) {
        toast.error('Failed to load business plans. Please verify Stripe configuration.');
      }
    }
    setLoadingBusinessPlans(false);
  };

  // Handle plan actions — the saga toasts and refreshes the list itself
  const handleToggleStatus = (planId) => dispatch(togglePlanStatus({ id: planId }));

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setShowEditModal(true);
  };

  // Delete an unpurchased custom plan (also archives its Stripe product/price and coupon)
  const handleDeleteCustomPlan = async () => {
    if (!planToDelete) return;
    setDeletingPlan(true);
    try {
      const response = await axios.delete(`/admin/billing/plans/${planToDelete['_id']}`);
      if (response['data']['code'] === 200) {
        toast.success('Custom plan deleted');
        setPlanToDelete(null);
        loadPlans();
      } else toast.error(response['data']['message'] || 'Failed to delete plan');
    } catch (error) {
      toast.error(error['response']?.['data']?.['message'] || 'Failed to delete plan');
    }
    setDeletingPlan(false);
  };

  // Re-run Stripe provisioning for a custom plan whose sync failed
  const handleRetryCustomPlanSync = async (planId) => {
    try {
      const response = await axios.post(`/admin/billing/plans/${planId}/retry-sync`);
      if (response['data']['code'] === 200) {
        toast.success('Plan synced with Stripe successfully');
        loadPlans();
      } else toast.error(response['data']['message'] || 'Failed to sync plan');
    } catch (error) {
      toast.error(error['response']?.['data']?.['message'] || 'Failed to sync plan');
    }
  };

  // Handle plan assignment (for admin)
  const handleAssignPlan = (plan) => {
    setSelectedPlanForAssign(plan);
    setShowAssignModal(true);
  };

  // Open the workspace-targeted purchase-link builder for a custom plan
  const handleWorkspaceLink = (plan) => {
    setWsLinkPlan(plan);
    setWsLinkWorkspace(null);
    setShowWsLinkModal(true);
  };

  const handleAssignPlanSubmit = async () => {
    if (!selectedWorkspaceForAssign || !selectedPlanForAssign) {
      toast.error('Please select a workspace');
      return;
    }

    setAssignmentLoading(true);
    try {
      const response = await axios.post('/admin/billing/plans/assign', {
        workspaceId: selectedWorkspaceForAssign['value'],
        planId: selectedPlanForAssign['_id'],
        reason: assignmentReason
      });

      if (response['data']['code'] === 200) {
        toast.success('Plan assigned successfully');
        setShowAssignModal(false);
        setSelectedWorkspaceForAssign(null);
        setAssignmentReason('');
      }
    } catch (error) {
      toast.error(error['response']?.['data']?.['message'] || 'Failed to assign plan');
    }
    setAssignmentLoading(false);
  };

  // Business plan pricing update
  const handleUpdateBusinessPlanPricing = async (planId, newPricing) => {
    try {
      const response = await axios.put(`/payment/agencyPlans/${planId}`, newPricing);
      if (response['data']['code'] === 200) {
        loadBusinessPlans();
        setShowPriceEditModal(false);
        toast.success('Plan pricing updated successfully');
      }
    } catch (error) {
      toast.error(error['response']?.['data']?.['message'] || 'Failed to update plan pricing');
    }
  }

  // Filter logic for plans
  const getFilteredPlans = () => {
    let plans = isAgencyView ? businessPlans : adminPlans;

    if (searchTerm) {
      plans = plans.filter(plan =>
        plan['name'].toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan['displayName']?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Chip filters: OR within a group (intervals / categories), AND across groups
    const intervalSel = typeChips.filter(chip => INTERVAL_CHIPS.some(c => c['value'] === chip));
    const categorySel = typeChips.filter(chip => CATEGORY_CHIPS.some(c => c['value'] === chip));
    if (intervalSel.length) plans = plans.filter(plan => intervalSel.includes(plan['interval']));
    if (categorySel.length) plans = plans.filter(plan => categorySel.some(chip => matchesCategoryChip(plan, chip)));
    if (statusFilter) plans = plans.filter(plan => plan['isActive'] === (statusFilter === 'active'));

    // Group by billing interval (Monthly → Quarterly → Yearly → Lifetime), cheapest first within each
    // group; copy before sorting so the redux array is never mutated
    const intervalRank = (plan) => INTERVAL_SORT_RANK[plan['interval']] ?? INTERVAL_CHIPS.length;
    return [...plans].sort((a, b) => (intervalRank(a) - intervalRank(b)) || ((a['price'] || 0) - (b['price'] || 0)) || (a['displayName'] || a['name'] || '').localeCompare(b['displayName'] || b['name'] || ''));
  };

  const toggleTypeChip = (value) => setTypeChips(prev => prev.includes(value) ? prev.filter(chip => chip !== value) : [...prev, value]);

  const toggleExpanded = (planId) => setExpandedPlans(prev => ({ ...prev, [planId]: !prev[planId] }));

  const formatFeature = (key, value) => {
    const featureLabels = {
      users: 'Users',
      contacts: 'Contacts',
      monthlyCredits: 'Monthly Credits',
      callingMinutes: 'Calling Minutes',
      agentExecutions: 'Agent Executions',
      preBuiltAgents: 'Pre-built Agents',
      emails: 'Emails',
      subWorkspaces: 'Sub-Workspaces',
      supportType: 'Support'
    };

    const label = featureLabels[key] || key;

    if (value === -1 || value === 'unlimited') return `Unlimited ${label}`;
    if (typeof value === 'boolean') return value ? `✓ ${label}` : `✗ ${label}`;
    if (typeof value === 'number') return `${value.toLocaleString()} ${label}`;
    return `${value} ${label}`;
  };

  const renderPlanCard = (plan) => {
    const isAgency = plan['creator'] == 'agency';
    const isCredits = plan['type'] === 'credits';
    const isCustomPlan = plan['metadata']?.['isCustom'] && plan['planType'] === 'doneForYou';
    const shareUrl = isCustomPlan && plan['shareToken'] ? `${window.location.origin}/plan/${plan['shareToken']}` : '';
    const isExpanded = !!expandedPlans[plan['_id']];
    const features = isCredits ? [] : plan['featuresWithIcons']?.length > 0
      ? plan['featuresWithIcons'].filter(feature => feature['text']).map(feature => feature['text'])
      : Object.entries(plan['features'] || {}).map(([key, value]) => formatFeature(key, value));
    if (!isCredits && plan['monthlyCredits']) features.push(`${plan['monthlyCredits'].toLocaleString()} Monthly Credits`);
    const visibleFeatures = isExpanded ? features : features.slice(0, 6);
    const bundleCredits = plan['creditsConfig']?.['credits'] || 0;
    const bonusCredits = plan['creditsConfig']?.['bonusCredits'] || 0;

    return (
      <div key={plan['_id']} className={`admin-plans-card ${!plan['isActive'] ? 'admin-plans-card-inactive' : ''}`}>
        <div className='admin-plans-card-header'>
          <h5 className='admin-plans-card-title'>{plan['displayName'] || plan['name']}</h5>
          <div className='admin-plans-card-price'>${formatNumber(plan['price'])}<span>{isCredits ? ' one-time' : `/${plan['interval'] || 'month'}`}</span></div>
        </div>
        <div className='admin-plans-card-pills'>
          <span className={`admin-plans-pill admin-plans-pill-${plan['planType'] || 'business'}`}>{PLAN_TYPE_PILL_LABELS[plan['planType']] || plan['planType']}</span>
          {INTERVAL_PILL_LABELS[plan['interval']] && <span className='admin-plans-pill admin-plans-pill-interval'>{INTERVAL_PILL_LABELS[plan['interval']]}</span>}
          <span className={`admin-plans-pill ${plan['isActive'] ? 'admin-plans-pill-active' : 'admin-plans-pill-inactive'}`}>• {plan['isActive'] ? 'Active' : 'Inactive'}</span>
          {plan['isPopular'] && <span className='admin-plans-pill admin-plans-pill-popular'>Popular</span>}
          {plan['type'] === 'credits' && <span className='admin-plans-pill admin-plans-pill-interval'>One-time</span>}
          {isCustomPlan && <span className='admin-plans-pill admin-plans-pill-popular'>Custom</span>}
          {isCustomPlan && plan['metadata']?.['discountPercent'] > 0 && <span className='admin-plans-pill admin-plans-pill-credits'>{plan['metadata']['discountPercent']}% first month</span>}
        </div>
        {plan['description'] && <p className='admin-plans-card-desc'>{plan['description']}</p>}
        {isCredits ? (
          <div className='admin-plans-credits-box'>
            <div className='admin-plans-credits-amount'>
              <i className='fa fa-database'></i>
              {plan['metadata']?.['isCustom'] ? 'Buyer chooses amount' : `${bundleCredits.toLocaleString()} credits`}
            </div>
            {bonusCredits > 0 && <div className='admin-plans-credits-bonus'>+{bonusCredits.toLocaleString()} bonus credits ({(bundleCredits + bonusCredits).toLocaleString()} total)</div>}
            {plan['metadata']?.['pricePerCredit'] > 0 && <div className='admin-plans-credits-rate'>${plan['metadata']['pricePerCredit']}/credit</div>}
          </div>
        ) : (
          <>
            <ul className='admin-plans-card-features'>
              {visibleFeatures.map((text, idx) => <li key={idx}><i className='fa fa-check'></i>{text}</li>)}
            </ul>
            {features.length > 6 && (
              <button className='admin-plans-more-btn' onClick={() => toggleExpanded(plan['_id'])}>
                {isExpanded ? 'Show less' : `+${features.length - 6} more`}
              </button>
            )}
          </>
        )}
        {isCustomPlan && (
          <div className='admin-plans-custom-info'>
            {plan['customerEmail'] && <div className='admin-plans-custom-row'><i className='fa fa-user mr-1'></i>{plan['customerEmail']}</div>}
            <div className='admin-plans-custom-row'>
              <i className='fa fa-building mr-1'></i>
              {plan['workspaceId'] ? `Purchased — ${plan['workspaceId']['name'] || plan['workspaceId']}` : 'Not yet purchased'}
            </div>
            {shareUrl && !plan['workspaceId'] && (
              <div className='admin-plans-link-row'>
                <input readOnly value={shareUrl} className='admin-plans-link-input' />
                <CopyToClipboard text={shareUrl} onCopy={() => toast.success('Plan page link copied!')}>
                  <button className='btn-style-new'><i className='fa fa-copy'></i></button>
                </CopyToClipboard>
              </div>
            )}
          </div>
        )}
        <div className='admin-plans-card-meta'>
          <div>Type: {plan['type']}</div>
          <div>ID: {plan['_id']}</div>
          {plan['stripeProductId'] && <div>Product: {plan['stripeProductId']}</div>}
          {plan['stripePriceId'] && <div>Price: {plan['stripePriceId']}</div>}
          {isAgency && plan['basePrice'] && <div>Base Price: ${plan['basePrice']}</div>}
        </div>
        <div className='admin-plans-card-actions'>
          {isAgency ? (
            <button className='btn-style-border' onClick={() => { setShowPriceEditModal(true); setSelectedPlanForPriceEdit(plan); }}>
              <i className='fa fa-edit mr-1'></i>Customize Pricing
            </button>
          ) : (
            <>
              <button className='btn-style-border' onClick={() => handleToggleStatus(plan['_id'])}>
                <i className={`fa fa-${plan['isActive'] ? 'pause' : 'play'} mr-1`}></i>{plan['isActive'] ? 'Deactivate' : 'Activate'}
              </button>
              <button className='btn-style-new' onClick={() => handleEditPlan(plan)}>
                <i className='fa fa-edit mr-1'></i>Edit
              </button>
              {accessType === 'admin' && !isCustomPlan && (
                <button className='btn-style-border' onClick={() => handleAssignPlan(plan)}>
                  <i className='fa fa-user-plus mr-1'></i>Assign
                </button>
              )}
              {accessType === 'admin' && isCustomPlan && !plan['workspaceId'] && (
                <button className='btn-style-border' onClick={() => handleWorkspaceLink(plan)}>
                  <i className='fa fa-link mr-1'></i>Workspace Link
                </button>
              )}
              {accessType === 'admin' && isCustomPlan && (
                <button className='btn-style-border' onClick={() => { setCreateSourcePlan(plan); setShowCreateModal(true); }}>
                  <i className='fa fa-clone mr-1'></i>Duplicate
                </button>
              )}
              {accessType === 'admin' && isCustomPlan && plan['stripeStatus'] === 'failed' && (
                <button className='btn-style-border' onClick={() => handleRetryCustomPlanSync(plan['_id'])}>
                  <i className='fa fa-refresh mr-1'></i>Retry Stripe Sync
                </button>
              )}
              {accessType === 'admin' && isCustomPlan && !plan['workspaceId'] && (
                <button className='btn-style-border admin-plans-delete-btn' onClick={() => setPlanToDelete(plan)}>
                  <i className='fa fa-trash mr-1'></i>Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const renderTableView = () => {
    const filteredPlans = getFilteredPlans();
    return (
      <Table responsive hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Price</th>
            <th>Status</th>
            <th>Features</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredPlans.map(plan => (
            <tr key={plan['_id']} className={!plan['isActive'] ? 'table-secondary' : ''}>
              <td>
                <div>
                  <strong>{plan['displayName'] || plan['name']}</strong>
                  {plan['isPopular'] && <Badge color="primary" className="ml-2">Popular</Badge>}
                  {plan['description'] && <div className="small text-muted">{plan['description']}</div>}
                </div>
              </td>
              <td>
                <div className='admin-plans-card-pills admin-plans-table-pills'>
                  <span className={`admin-plans-pill admin-plans-pill-${plan['planType'] || 'business'}`}>{PLAN_TYPE_PILL_LABELS[plan['planType']] || plan['planType']}</span>
                  {INTERVAL_PILL_LABELS[plan['interval']] && <span className='admin-plans-pill admin-plans-pill-interval'>{INTERVAL_PILL_LABELS[plan['interval']]}</span>}
                  {plan['type'] === 'credits' && <span className='admin-plans-pill admin-plans-pill-interval'>One-time</span>}
                  {plan['metadata']?.['isCustom'] && plan['planType'] === 'doneForYou' && <span className='admin-plans-pill admin-plans-pill-popular'>Custom</span>}
                </div>
              </td>
              <td>${formatNumber(plan['price'])}{plan['type'] === 'credits' ? ' one-time' : `/${plan['interval'] || 'month'}`}</td>
              <td>
                <span className={`admin-plans-pill ${plan['isActive'] ? 'admin-plans-pill-active' : 'admin-plans-pill-inactive'}`}>• {plan['isActive'] ? 'Active' : 'Inactive'}</span>
              </td>
              <td>
                <small>
                  {Object.entries(plan['features'] || {})
                    .slice(0, 3)
                    .map(([key, value]) => formatFeature(key, value))
                    .join(', ')}
                  ...
                </small>
              </td>
              <td className="text-center">
                <div className='action-box actionbox-plan'>
                  {plan['isAgencyBusinessPlan'] ? (
                    <button
                      className='action-btn' title="Edit Pricing"
                      size="sm"
                      onClick={() => {
                        setSelectedPlanForPriceEdit(plan);
                        setShowPriceEditModal(true);
                      }}
                    >
                      <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#A46497"></circle><path d="M13.5 17.375H10.5C7.785 17.375 6.625 16.215 6.625 13.5V10.5C6.625 7.785 7.785 6.625 10.5 6.625H11.5C11.705 6.625 11.875 6.795 11.875 7C11.875 7.205 11.705 7.375 11.5 7.375H10.5C8.195 7.375 7.375 8.195 7.375 10.5V13.5C7.375 15.805 8.195 16.625 10.5 16.625H13.5C15.805 16.625 16.625 15.805 16.625 13.5V12.5C16.625 12.295 16.795 12.125 17 12.125C17.205 12.125 17.375 12.295 17.375 12.5V13.5C17.375 16.215 16.215 17.375 13.5 17.375Z" fill="#A46497"></path><path d="M10.2497 14.845C9.94467 14.845 9.66467 14.735 9.45967 14.535C9.21467 14.29 9.10967 13.935 9.16467 13.56L9.37967 12.055C9.41967 11.765 9.60967 11.39 9.81467 11.185L13.7547 7.245C14.7497 6.25 15.7597 6.25 16.7547 7.245C17.2997 7.79 17.5447 8.345 17.4947 8.9C17.4497 9.35 17.2097 9.79 16.7547 10.24L12.8147 14.18C12.6097 14.385 12.2347 14.575 11.9447 14.615L10.4397 14.83C10.3747 14.845 10.3097 14.845 10.2497 14.845ZM14.2847 7.775L10.3447 11.715C10.2497 11.81 10.1397 12.03 10.1197 12.16L9.90467 13.665C9.88467 13.81 9.91467 13.93 9.98967 14.005C10.0647 14.08 10.1847 14.11 10.3297 14.09L11.8347 13.875C11.9647 13.855 12.1897 13.745 12.2797 13.65L16.2197 9.71C16.5447 9.385 16.7147 9.095 16.7397 8.825C16.7697 8.5 16.5997 8.155 16.2197 7.77C15.4197 6.97 14.8697 7.195 14.2847 7.775Z" fill="#A46497"></path><path d="M15.9248 10.915C15.8898 10.915 15.8548 10.91 15.8248 10.9C14.5098 10.53 13.4648 9.485 13.0948 8.17C13.0398 7.97 13.1548 7.765 13.3548 7.705C13.5548 7.65 13.7598 7.765 13.8148 7.965C14.1148 9.03 14.9598 9.875 16.0248 10.175C16.2248 10.23 16.3398 10.44 16.2848 10.64C16.2398 10.81 16.0898 10.915 15.9248 10.915Z" fill="#A46497"></path></svg></i>
                    </button>
                  ) : isAgencyView ? (
                    <span className="text-muted">-</span>
                  ) : (
                    <UncontrolledDropdown>
                      <DropdownToggle color="secondary" size="sm">
                        <i className="fa fa-ellipsis-h"></i>
                      </DropdownToggle>
                      <DropdownMenu right>
                        <DropdownItem onClick={() => handleEditPlan(plan)}>
                          <i className="fa fa-edit mr-2"></i> Edit
                        </DropdownItem>
                        <DropdownItem onClick={() => handleToggleStatus(plan['_id'])}>
                          <i className={`fa fa-${plan['isActive'] ? 'pause' : 'play'} mr-2`}></i>
                          {plan['isActive'] ? 'Deactivate' : 'Activate'}
                        </DropdownItem>
                        {accessType === 'admin' && (
                          <DropdownItem onClick={() => handleAssignPlan(plan)}>
                            <i className="fa fa-user-plus mr-2"></i> Assign
                          </DropdownItem>
                        )}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  )}
                </div>

              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  // Loading state - show loader while fetching initial gateway status
  if (isAgencyView && gatewayLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="text-muted mb-0">Loading payment gateway status...</p>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Container fluid className="px-0">
      {/* Payment Gateway Section - Only show for Agency users AND when not configured */}
      {isAgencyView && !gatewayStatus?.['isConfigured'] && (
        <Row className="justify-content-center mb-4">
          <Col lg="8" md="10">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h4><i className="fas fa-credit-card mr-2"></i> Stripe Payment Gateway</h4>
                    {/* <CardTitle tag="h4" className="mb-0">
                      <i className="fas fa-credit-card mr-2"></i>
                      Stripe Payment Gateway
                    </CardTitle> */}
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                {/* Always show the configuration form when the card is displayed */}
                <>
                  <Alert color="warning" className="mb-4">
                    <strong>Stripe Configuration Required</strong>
                    <div className="mt-1">
                      Configure your Stripe account to activate and manage subscription plans. You can still create plans as drafts.
                    </div>
                  </Alert>

                  <Form onSubmit={handleSubmit}>
                    {errors['environment'] && <Alert color="danger">{errors['environment']}</Alert>}

                    <FormGroup className="stripe-group">
                      <Label for="secretKey">
                        Stripe Secret Key <span className="text-danger">*</span>
                      </Label>
                      <InputGroup>
                        <Input
                          type={showSecretKey ? 'text' : 'password'}
                          name="secretKey"
                          id="secretKey"
                          placeholder="sk_test_... or sk_live_..."
                          value={formData['secretKey']}
                          onChange={handleInputChange}
                          invalid={!!errors['secretKey']}
                        />
                        <InputGroupText className="cursor-pointer" onClick={() => setShowSecretKey(!showSecretKey)}>
                          <i className={`fas fa-${showSecretKey ? 'eye-slash' : 'eye'}`}></i>
                        </InputGroupText>
                      </InputGroup>
                      {errors['secretKey'] && (
                        <div className="invalid-feedback d-block">{errors['secretKey']}</div>
                      )}
                      <small className="form-text text-muted">
                        Your Stripe secret key (starts with sk_test_ or sk_live_)
                        <strong className="text-warning"> - Keep this secure!</strong>
                      </small>
                    </FormGroup>

                    <Alert color="info" className="mt-3">
                      <i className="fas fa-info-circle mr-2"></i>
                      <strong>Automatic Setup:</strong> We'll automatically create the webhook endpoint and sync your business plans to your Stripe account.
                    </Alert>

                    <button color="primary" type="submit" disabled={validating} className="mt-3 btn-style-new">
                      {validating ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          Connecting & Setting Up...
                        </>
                      ) : <> <i className="fas fa-check mr-2"></i> Connect Stripe </>
                      }
                    </button>
                  </Form>

                  <Alert color="secondary" className="mt-4">
                    <h6>What happens next:</h6>
                    <ol className="pl-3 mb-0">
                      <li>We'll validate your Stripe account credentials</li>
                      <li>Automatically create a webhook endpoint in your Stripe dashboard</li>
                      <li>Sync all business plans as products in your Stripe account</li>
                      <li>You'll be ready to accept payments immediately!</li>
                    </ol>
                  </Alert>
                </>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Plans Section */}
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-2">

          {/* Show Stripe status badge for agency users when configured */}
          {isAgencyView && gatewayStatus?.['isConfigured'] && (
            <>
              <h3 className="mb-0">
                <i className="fas fa-cube mr-2"></i>
                Subscription Plans
              </h3>
              <div className="d-flex align-items-center gap-2">
                <Badge color="success" pill>
                  <i className="fas fa-check-circle mr-1"></i>
                  Stripe Connected
                </Badge>
                {gatewayStatus['liveMode'] ?
                  <Badge color="danger" pill>LIVE MODE</Badge>
                  : <Badge color="warning" pill>TEST MODE</Badge>}
                <button className="btn-style-new" size="sm" color="link" onClick={() => setIsGatewayCollapsed(!isGatewayCollapsed)}>
                  <i className={`fas fa-chevron-${isGatewayCollapsed ? 'down' : 'up'}`}></i>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Collapsible Stripe Configuration Details - Only for agency when configured */}
        {isAgencyView && gatewayStatus?.['isConfigured'] && (
          <Collapse isOpen={!isGatewayCollapsed}>
            <Card className="mb-4">
              <CardBody>
                <Row className="mb-3">
                  <Col md={6}>
                    <p className="mb-1"> <strong>Account:</strong> {gatewayStatus['accountName'] || 'Stripe Account'} </p>
                    <p className="mb-1"> <strong>Account ID:</strong> {gatewayStatus['accountId']} </p>
                    <p className="mb-1"> <strong>Email:</strong> {gatewayStatus['accountEmail']} </p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Configured:</strong> {new Date(gatewayStatus['configuredAt']).toLocaleDateString()}
                    </p>
                    <p className="mb-1">
                      <strong>Last Validated:</strong> {gatewayStatus['lastValidated'] ? new Date(gatewayStatus['lastValidated']).toLocaleDateString() : 'Never'}
                    </p>
                    <p className="mb-1">
                      <strong>Capabilities:</strong> {gatewayStatus['capabilities']?.join(', ') || 'None'}
                    </p>
                  </Col>
                </Row>
                <div className="mt-3">
                  <button color="info" size="sm" onClick={handleValidate} disabled={validating} className="mr-2 btn-style-new">
                    {validating ? <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                      : <i className="fas fa-check mr-1"></i>}
                    Validate Connection
                  </button>
                  <button color="danger" className="btn-style-border" size="sm" onClick={() => setShowRemoveModal(true)}>
                    <i className="fas fa-trash mr-1"></i>
                    Remove Configuration
                  </button>
                </div>

                {/* Webhook Configuration Info */}
                {gatewayStatus?.['webhookUrl'] && (
                  <Alert color="info" className="mt-4">
                    <h6>Webhook Configuration</h6>
                    <InputGroup className="mb-2 webhook-group">
                      <Input readOnly type="text" id="webhook-url" value={gatewayStatus['webhookUrl']} />
                      <CopyToClipboard text={gatewayStatus['webhookUrl']} onCopy={handleCopyWebhook}>
                        <button className="btn-style-new" color="secondary">
                          <i className="fas fa-copy mr-1"></i>
                          Copy
                        </button>
                      </CopyToClipboard>
                    </InputGroup>
                    <UncontrolledTooltip placement="top" target="webhook-url">
                      Add this URL to your Stripe webhook endpoints
                    </UncontrolledTooltip>
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Collapse>
        )}

        {/* Plan Assignment Section removed for agency view - keeping only for admin users */}

        {/* Plan Assignment for Admin Users */}
        {accessType === 'admin' && (
          <Alert color="info">
            <i className="fa fa-info-circle mr-2"></i>
            You can assign plans directly to workspaces for special cases or testing purposes.
          </Alert>
        )}

        {/* Search and Filter Bar - Always show when there are plans to display */}
        <div className='content-dashboard'>
          <div className="table-container filters-table">
            {(!isAgencyView || gatewayStatus?.['isConfigured']) && (isAgencyView ? businessPlans.length > 0 : adminPlans.length > 0) && (
              <>
                {/* Filters */}
                <div className='admin-plans-filter-bar'>
                  <div className='admin-plans-filter-row'>
                    <input
                      type='text'
                      className='admin-plans-search-input'
                      value={searchTerm}
                      placeholder='Search plans...'
                      onChange={(e) => setSearchTerm(e['target']['value'])}
                    />
                    <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e['target']['value'])} className="admin-plans-status-select">
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Input>
                    <div className='admin-plans-filter-chips'>
                      {[...INTERVAL_CHIPS, ...CATEGORY_CHIPS].map(chip => (
                        <button key={chip['value']} className={`admin-plans-filter-chip admin-plans-filter-chip-${chip['value']} ${typeChips.includes(chip['value']) ? 'selected' : ''}`} onClick={() => toggleTypeChip(chip['value'])}>
                          {chip['label']}
                        </button>
                      ))}
                    </div>
                    <div className='admin-plans-filter-right'>
                    {accessType === 'admin' && !isAgencyView && (
                      <button className='admin-plans-create-btn' onClick={() => { setCreateSourcePlan(null); setShowCreateModal(true); }}>
                        + Create Custom Plan
                      </button>
                    )}
                    <div className='filter-table-select'>
                      <button className={`filters-button ${viewMode === 'card' ? 'primary' : 'active'}`} onClick={() => setViewMode('card')}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.82449 13.2707H2.46783C1.29533 13.2707 0.729492 12.7282 0.729492 11.6082V2.3915C0.729492 1.2715 1.30116 0.729004 2.46783 0.729004H4.82449C5.99699 0.729004 6.56283 1.2715 6.56283 2.3915V11.6082C6.56283 12.7282 5.99116 13.2707 4.82449 13.2707ZM2.46783 1.604C1.72699 1.604 1.60449 1.80234 1.60449 2.3915V11.6082C1.60449 12.1973 1.72699 12.3957 2.46783 12.3957H4.82449C5.56533 12.3957 5.68783 12.1973 5.68783 11.6082V2.3915C5.68783 1.80234 5.56533 1.604 4.82449 1.604H2.46783V1.604Z" fill="currentColor" />
                          <path d="M11.5325 9.18734H9.17583C8.00333 9.18734 7.4375 8.64484 7.4375 7.52484V2.3915C7.4375 1.2715 8.00917 0.729004 9.17583 0.729004H11.5325C12.705 0.729004 13.2708 1.2715 13.2708 2.3915V7.52484C13.2708 8.64484 12.6992 9.18734 11.5325 9.18734ZM9.17583 1.604C8.435 1.604 8.3125 1.80234 8.3125 2.3915V7.52484C8.3125 8.114 8.435 8.31234 9.17583 8.31234H11.5325C12.2733 8.31234 12.3958 8.114 12.3958 7.52484V2.3915C12.3958 1.80234 12.2733 1.604 11.5325 1.604H9.17583V1.604Z" fill="currentColor" />
                        </svg>
                      </button>
                      <button className={`filters-button ${viewMode === 'table' ? 'primary' : 'active'}`} onClick={() => setViewMode('table')}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11.6087 13.2708H2.39199C1.27199 13.2708 0.729492 12.6992 0.729492 11.5325V9.17583C0.729492 8.00333 1.27199 7.4375 2.39199 7.4375H11.6087C12.7287 7.4375 13.2712 8.00917 13.2712 9.17583V11.5325C13.2712 12.6992 12.7287 13.2708 11.6087 13.2708ZM2.39199 8.3125C1.80283 8.3125 1.60449 8.435 1.60449 9.17583V11.5325C1.60449 12.2733 1.80283 12.3958 2.39199 12.3958H11.6087C12.1978 12.3958 12.3962 12.2733 12.3962 11.5325V9.17583C12.3962 8.435 12.1978 8.3125 11.6087 8.3125H2.39199Z" fill="currentColor" />
                          <path d="M11.6087 6.56234H2.39199C1.27199 6.56234 0.729492 5.99067 0.729492 4.824V2.46734C0.729492 1.29484 1.27199 0.729004 2.39199 0.729004H11.6087C12.7287 0.729004 13.2712 1.30067 13.2712 2.46734V4.824C13.2712 5.99067 12.7287 6.56234 11.6087 6.56234ZM2.39199 1.604C1.80283 1.604 1.60449 1.7265 1.60449 2.46734V4.824C1.60449 5.56484 1.80283 5.68734 2.39199 5.68734H11.6087C12.1978 5.68734 12.3962 5.56484 12.3962 4.824V2.46734C12.3962 1.7265 12.1978 1.604 11.6087 1.604H2.39199V1.604Z" fill="currentColor" />
                        </svg>
                      </button>
                    </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className='table-area'>
              {/* Plans Display */}
              {(!isAgencyView || gatewayStatus?.['isConfigured']) && (
                loadingBusinessPlans || adminPlansLoading ? (
                  <Row>
                    <Col className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading plans...</span>
                      </div>
                    </Col>
                  </Row>
                ) : viewMode === 'card' ? (
                  <Row>
                    {getFilteredPlans().length > 0
                      ? <div className='admin-plans-area'>
                        <Masonry
                          breakpointCols={breakpoints}
                          className="masonry-grid"
                          columnClassName="masonry-column"
                        >
                          {getFilteredPlans().map(renderPlanCard)}
                        </Masonry>
                      </div>
                      :
                      <Col className="text-center py-5">
                        <h4 className="text-muted no-plans-message">No plans found</h4>
                      </Col>
                    }
                  </Row>
                ) : renderTableView()
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Edit Plan Modal */}
      {showEditModal && selectedPlan && (
        <PlanEditModal
          plan={selectedPlan}
          isOpen={showEditModal}
          onUpdate={() => { setShowEditModal(false); loadPlans(); }}
          toggle={() => setShowEditModal(false)}
        />
      )}

      {/* Create / Duplicate Custom Plan Modal */}
      {showCreateModal && (
        <PlanEditModal
          plan={null}
          sourcePlan={createSourcePlan}
          plansList={adminPlans}
          defaultsPlan={adminPlans.find(p => p['price'] === 0 && p['planType'] === 'business' && p['type'] === 'subscription')}
          isOpen={showCreateModal}
          onUpdate={() => { setShowCreateModal(false); setCreateSourcePlan(null); loadPlans(); }}
          toggle={() => { setShowCreateModal(false); setCreateSourcePlan(null); }}
        />
      )}

      {/* Delete Custom Plan Confirmation */}
      <Modal isOpen={!!planToDelete} toggle={() => setPlanToDelete(null)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setPlanToDelete(null)}>
          <h3>Delete Custom Plan</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4'>
            <Alert color="danger">
              <h5 className="warning-text"><i className="fa fa-exclamation-triangle mr-2"></i><strong>Warning!</strong> Deleting "{planToDelete?.['displayName'] || planToDelete?.['name']}" will:</h5>
              <ul className="mt-2 mb-0 warning-text warning-list">
                <li>Permanently remove the plan from the database</li>
                <li>Disable its public payment page link</li>
                <li>Deactivate its Stripe price and delete its discount coupon</li>
              </ul>
            </Alert>
            <p>Are you sure you want to delete this custom plan?</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-border" onClick={() => setPlanToDelete(null)}>
            Cancel
          </button>
          <button className="btn-style-new" onClick={handleDeleteCustomPlan} disabled={deletingPlan}>
            {deletingPlan ? (
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="fa fa-trash mr-2"></i>
            )}
            Delete Plan
          </button>
        </ModalFooter>
      </Modal>

      {/* Price Edit Modal for Business Plans */}
      <Modal isOpen={showPriceEditModal} toggle={() => setShowPriceEditModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowPriceEditModal(false)}>
          <h3>Customize Plan Pricing</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4'>
            {selectedPlanForPriceEdit && (
              <Form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e['target']);
                handleUpdateBusinessPlanPricing(selectedPlanForPriceEdit['_id'], {
                  displayName: formData.get('displayName'),
                  customPrice: parseFloat(formData.get('customPrice')),
                  customMonthlyCredits: parseInt(formData.get('customMonthlyCredits'))
                });
              }}>
                <h5>{selectedPlanForPriceEdit['displayName']}</h5>
                <p className="text-muted">Customize pricing for your agency</p>

                <FormGroup>
                  <Label for="displayName">Plan Display Name</Label>
                  <Input
                    type="text"
                    name="displayName"
                    id="displayName"
                    defaultValue={selectedPlanForPriceEdit['displayName']}
                    placeholder="Enter plan display name"
                    required
                  />
                  <small className="text-muted">
                    This is the name your customers will see
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label>Base Price</Label>
                  <Input
                    type="text"
                    value={`$${selectedPlanForPriceEdit['basePrice'] || selectedPlanForPriceEdit['price']}`}
                    disabled
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="customPrice">Your Price</Label>
                  <InputGroup>
                    <InputGroupText>$</InputGroupText>
                    <Input
                      type="number"
                      name="customPrice"
                      id="customPrice"
                      min={selectedPlanForPriceEdit['basePrice'] || selectedPlanForPriceEdit['price']}
                      step="0.01"
                      defaultValue={selectedPlanForPriceEdit['price']}
                      required
                    />
                  </InputGroup>
                  <small className="text-muted">
                    Minimum: ${selectedPlanForPriceEdit['basePrice'] || selectedPlanForPriceEdit['price']}
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label for="customMonthlyCredits">Monthly Credits</Label>
                  <Input
                    type="number"
                    name="customMonthlyCredits"
                    id="customMonthlyCredits"
                    min="0"
                    defaultValue={selectedPlanForPriceEdit['monthlyCredits']}
                  />
                </FormGroup>

                <div className="d-flex justify-content-end">
                  <button type="button" className="mr-2 btn-style-border" onClick={() => setShowPriceEditModal(false)}>
                    Cancel
                  </button>
                  <button className="btn-style-new" type="submit">
                    Save Changes
                  </button>
                </div>
              </Form>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Remove Gateway Modal */}
      <Modal isOpen={showRemoveModal} toggle={() => setShowRemoveModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowRemoveModal(false)}>
          <h3>Remove Payment Gateway</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4'>
            <Alert color="danger">
              <h5 className="warning-text"><i className="fa fa-exclamation-triangle mr-2"></i> <strong>Warning! </strong> Removing the payment gateway will:</h5>
              <ul className="mt-2 mb-0 warning-text warning-list">
                <li>Disconnect your Stripe account</li>
                <li>Prevent new subscriptions from being created</li>
                <li>Disable payment processing for sub-workspaces</li>
                <li>Remove webhook configuration from Stripe</li>
              </ul>
            </Alert>
            <p>Are you sure you want to remove the payment gateway configuration?</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-border" onClick={() => setShowRemoveModal(false)}>
            Cancel
          </button>
          <button className="btn-style-new" onClick={handleRemoveGateway} disabled={loading}>
            {loading ? (
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="fa fa-trash mr-2"></i>
            )}
            Remove Gateway
          </button>
        </ModalFooter>
      </Modal>

      {/* Assign Plan Modal (Admin Only) */}
      <Modal isOpen={showAssignModal} toggle={() => setShowAssignModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowAssignModal(false)}>
          <h3>Assign Plan to Workspace</h3>
        </ModalHeader>
        <ModalBody>
          {selectedPlanForAssign && (
            <div className='content mt-4'>
              <h5>Plan: {selectedPlanForAssign['displayName'] || selectedPlanForAssign['name']}</h5>
              <p className="text-muted">Price: ${formatNumber(selectedPlanForAssign['price'])}/{selectedPlanForAssign['interval'] || 'month'}</p>

              <FormGroup>
                <Label for="workspace">Select Workspace</Label>
                <Select
                  id="workspace"
                  value={selectedWorkspaceForAssign}
                  onChange={setSelectedWorkspaceForAssign}
                  options={workspaces.map(ws => ({ value: ws['_id'], label: `${ws['name']} (${ws['ownerEmail']})` }))}
                  filterOption={filterWorkspaceOption}
                  placeholder="Search by name or workspace ID..."
                />
              </FormGroup>

              <FormGroup>
                <Label for="reason">Reason for Assignment</Label>
                <Input
                  type="textarea"
                  id="reason"
                  value={assignmentReason}
                  onChange={(e) => setAssignmentReason(e['target']['value'])}
                  placeholder="Optional: Provide a reason for this assignment..."
                  rows={3}
                />
              </FormGroup>

              <Alert color="info">
                <i className="fa fa-info-circle mr-2"></i>
                This will override the workspace's current subscription and assign this plan directly.
              </Alert>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-border" color="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </button>
          <button
            className="btn-style-new"
            onClick={handleAssignPlanSubmit}
            disabled={!selectedWorkspaceForAssign || assignmentLoading}
          >
            {assignmentLoading ? (
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="fa fa-check mr-2"></i>
            )}
            Assign Plan
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showWsLinkModal} toggle={() => setShowWsLinkModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowWsLinkModal(false)}>
          <h3>Workspace Purchase Link</h3>
        </ModalHeader>
        <ModalBody>
          {wsLinkPlan && (
            <div className='content mt-4'>
              <h5>Plan: {wsLinkPlan['displayName'] || wsLinkPlan['name']}</h5>
              <p className="text-muted">Price: ${formatNumber(wsLinkPlan['price'])}/{wsLinkPlan['interval'] || 'month'}</p>

              <FormGroup>
                <Label for="ws-link-workspace">Attach to Workspace</Label>
                <Select
                  id="ws-link-workspace"
                  value={wsLinkWorkspace}
                  onChange={setWsLinkWorkspace}
                  options={workspaces.map(ws => ({ value: ws['_id'], label: `${ws['name']}${ws['ownerEmail'] ? ` (${ws['ownerEmail']})` : ''}` }))}
                  filterOption={filterWorkspaceOption}
                  placeholder="Search by name or workspace ID..."
                />
              </FormGroup>

              {wsLinkWorkspace && (
                <FormGroup>
                  <Label>Share this link</Label>
                  <div className='admin-plans-link-row'>
                    <input readOnly value={`${window.location.origin}/plan/${wsLinkPlan['shareToken']}?ws=${wsLinkWorkspace['value']}`} className='admin-plans-link-input' />
                    <CopyToClipboard text={`${window.location.origin}/plan/${wsLinkPlan['shareToken']}?ws=${wsLinkWorkspace['value']}`} onCopy={() => toast.success('Workspace link copied!')}>
                      <button className='btn-style-new'><i className='fa fa-copy'></i></button>
                    </CopyToClipboard>
                  </div>
                </FormGroup>
              )}

              <Alert color="info">
                <i className="fa fa-info-circle mr-2"></i>
                When paid, this plan attaches to the selected workspace and its owner — no new account or workspace is created.
              </Alert>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-border" onClick={() => setShowWsLinkModal(false)}>
            Close
          </button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

// Reason: react-select filters on label text only — also match the option's value (workspace _id) so admins can paste an id
const filterWorkspaceOption = (option, rawInput) => {
  const input = (rawInput || '').trim().toLowerCase();
  if (!input) return true;
  return String(option['label'] || '').toLowerCase().includes(input) || String(option['value'] || '').toLowerCase().includes(input);
};

// Filter chip groups — OR within a group, AND across groups; chip order doubles as the card sort order
const INTERVAL_CHIPS = ['month', 'quarter', 'year', 'lifetime'].map(value => ({ value, label: INTERVAL_PILL_LABELS[value] }));
const INTERVAL_SORT_RANK = Object.fromEntries(INTERVAL_CHIPS.map((chip, index) => [chip['value'], index]));
const CATEGORY_CHIPS = [{ value: 'business', label: 'Business' }, { value: 'agency', label: 'Agency' }, { value: 'doneForYou', label: 'Done For You' }, { value: 'credits', label: 'Credits' }, { value: 'custom', label: 'Custom' }];

// Reason: the pay-what-you-want credit bundle also has metadata.isCustom — the planType qualifier keeps it under Credits
const matchesCategoryChip = (plan, chip) => {
  if (chip === 'credits') return plan['type'] === 'credits' || plan['planType'] === 'credits';
  if (chip === 'custom') return plan['metadata']?.['isCustom'] === true && plan['planType'] === 'doneForYou';
  return plan['planType'] === chip;
};

export default Plans;